import React, { useState, useEffect, useRef, useCallback } from "react";
import { StickyNavigationProps } from "./types";

const PAGE_SCROLL_POSITION_TO_ALLOW_MENU_VISIBILITY_TO_CHANGE = 1;
const OFFSET_SCROLL_TO_CHANGE_VISIBILITY = 20;
const THROTTLE_MS = 200;

const StickyNavigation: React.FC<StickyNavigationProps> = ({ mobileMenuOpen, children }) => {
  const [navVisible, setNavVisible] = useState(true);
  const directionRef = useRef(0);
  const previousScrollPositionRef = useRef(0);
  const positionAtLastChangeRef = useRef(0);
  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollHandler = useCallback(() => {
    const scrollTop =
      document.documentElement && document.documentElement.scrollTop
        ? document.documentElement.scrollTop
        : document.body.scrollTop;

    if (scrollTop > previousScrollPositionRef.current && directionRef.current > -1) {
      directionRef.current = -1;
      positionAtLastChangeRef.current = scrollTop;
    } else if (scrollTop < previousScrollPositionRef.current && directionRef.current < 1) {
      directionRef.current = 1;
      positionAtLastChangeRef.current = scrollTop;
    }

    let shouldBeVisible = null;

    if (
      Math.abs(scrollTop - positionAtLastChangeRef.current) >
      OFFSET_SCROLL_TO_CHANGE_VISIBILITY
    ) {
      shouldBeVisible = !(directionRef.current < 0);
    }

    if (scrollTop < PAGE_SCROLL_POSITION_TO_ALLOW_MENU_VISIBILITY_TO_CHANGE) {
      shouldBeVisible = true;
    }

    if (shouldBeVisible !== null) {
      setNavVisible(shouldBeVisible);
    }

    previousScrollPositionRef.current = scrollTop;
  }, []);

  useEffect(() => {
    const throttledHandler = () => {
      if (throttleTimerRef.current) return;
      throttleTimerRef.current = setTimeout(() => {
        scrollHandler();
        throttleTimerRef.current = null;
      }, THROTTLE_MS);
    };

    window.addEventListener("scroll", throttledHandler);
    window.addEventListener("resize", throttledHandler);
    scrollHandler();

    return () => {
      window.removeEventListener("scroll", throttledHandler);
      window.removeEventListener("resize", throttledHandler);
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
    };
  }, [scrollHandler]);

  const headerClass = [
    "transition-all h-[64px] md:h-[78px] fixed z-1000 w-full top-0",
  ];
  if (!navVisible)
    headerClass.push("translate-y-[-64px] md:translate-y-[-82px]");
  if (navVisible && mobileMenuOpen)
    headerClass.push("translate-y-[-64px] md:translate-y-[-82px]");

  return (
    <header className={headerClass.join(" ")}>
      <div className="header">
        <div className="header__wrapper">{children}</div>
      </div>
    </header>
  );
};

export default StickyNavigation;
